import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { getTaxPaymentPeriodsAsync } from '../../store/slices/tax/taxThunks'
import AddAdditionalPeriodsModal from '../../components/tax/AddAdditionalPeriodsModal'
import { Button, Grid, Row, Col, Content, Text, FlexboxGrid, Stack } from 'rsuite'
import dayjs from 'dayjs'
import { selectIsGetTaxPaymentPeriodsErrorMsg, selectIsGetTaxPaymentPeriodsInProgress, selectIsGetTaxPaymentPeriodsSuccess, selectPreSelectedTaxPeriod, selectTaxFlowData, selectTaxSteps } from '../../store/slices/tax/taxSelectors'
import IconIndicator from '../../components/user-feedback/IconIndicator'
import Notice from '../../components/user-feedback/Notice'
import TaxReportingStatusIndicator from '../../components/tax/TaxReportingStatusIndicator'
import PortalHeader from '../../components/layout/PortalHeader'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import { clearPreSelectedTaxPeriod, resetTaxState, updateTaxFlowData } from '../../store/slices/tax/taxSlice'
import { C_TOT_PAYMENT_STATUS_INCOMPLETE } from '../../common/consts'
import { getAddressWithUnitNumber, titleize } from '../../common/utils'
import { selectConfigExpressRegistrationTypeVerbiage, selectConfigTaxPaymentPeriod } from '../../store/slices/config/configSelectors'
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../common/enums'
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox'
import pluralize from 'pluralize'
import usePlaceNavigate from '../../hooks/usePlaceNavigate'

const TaxSelectProperties = () => {
    const dispatch = useDispatch();
    const placeNavigate = usePlaceNavigate();

    const [additionalPeriodsModalOpen, setAdditionalPeriodsModalOpen] = useState(false);
    const [availabelReportingPeriods, setAvailabelReportingPeriods] = useState(undefined);
    const [showNoSelectedLicneseError, setShowNoSelectedLicneseError] = useState(false);
    const [hideInactivePermits, setHideInactivePermits] = useState(true);

    const formData = useSelector(selectTaxFlowData); // used when navigating back to this page from a different page in the flow
    const paymentPeriodsInProgress = useSelector(selectIsGetTaxPaymentPeriodsInProgress);
    const paymentPeriodsSuccess = useSelector(selectIsGetTaxPaymentPeriodsSuccess);
    const paymentPeriodsErrorMessage = useSelector(selectIsGetTaxPaymentPeriodsErrorMsg);
    const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);
    const preSelectedPeriod = useSelector(selectPreSelectedTaxPeriod);
    const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);

    useEffect(() => {
        dispatch(getTaxPaymentPeriodsAsync(generateSelectableTaxablePeriods));
    }, [formData, taxPaymentPeriod, preSelectedPeriod]);

    const selectedReportingPeriods = useMemo(() => {
        return (availabelReportingPeriods?.filter(period => period.selected) || [])
            .sort((a, b) => dayjs(b?.startDate).isAfter(dayjs(a?.startDate)) ? 1 : -1)
            .map(period => ({
                ...period,
                licenses: (period.licenses?.filter(license => hideInactivePermits ? !license.isInactive : true) || [])
            }));
    }, [availabelReportingPeriods, hideInactivePermits]);

    const generateSelectableTaxablePeriods = useCallback((paymentPeriods) => {
        if (taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY) {
            generateReportingPeriodsByQuarters(paymentPeriods);
        }
        else {
            generateReportingPeriodsByMonth(paymentPeriods);
        }
    }, [taxPaymentPeriod, formData, preSelectedPeriod]);


    const getQuarterStartEndDates = (year, quarter) => {
        const startMonth = (quarter - 1) * 3; // 0, 3, 6, 9 for Q1, Q2, Q3, Q4
        const startDate = dayjs(new Date(year, startMonth, 1));
        const endDate = startDate.add(2, 'month').endOf('month').startOf('day');;
        return { startDate, endDate };
    };

    const generateReportingPeriodsByMonth = (paymentPeriods) => {
        const currentDate = dayjs();

        // Generate the list of the last 36 months, not including the current month
        const months = []
        for (let i = 1; i < 36; i++) {
            const date = currentDate.subtract(i, 'month')

            const startDate = date.startOf('month').format('YYYY-MM-DD');
            const endDate = date.endOf('month').format('YYYY-MM-DD');

            // Find payment periods matching the start and end dates 
            const matchingPeriod = paymentPeriods.find(period => datesMatch(period.startDate, startDate) && datesMatch(period.endDate, endDate));

            const selectedLicenses = formData?.selectedLicenses ?? [];
            const existingData = selectedLicenses.find(period => datesMatch(period.startDate, startDate) && datesMatch(period.endDate, endDate));

            let isSelectedOnLoad = false;
            if (preSelectedPeriod && preSelectedPeriod.startDate && preSelectedPeriod.endDate) {
                if (dayjs(preSelectedPeriod.startDate).isSame(dayjs(startDate)) &&
                    dayjs(preSelectedPeriod.endDate).isSame(dayjs(endDate))) {
                    isSelectedOnLoad = true;
                }
            }
            else if (existingData) {
                isSelectedOnLoad = true;
            }

            // Add selected property to all licenses if matching period found, otherwise use empty array
            const licenses = (matchingPeriod?.licenses ?? []).map(license => {
                // Check for matching license in existingData
                const existingLicense = existingData?.licenses.find(existing => existing.license === license.license);

                // Use existing license data if found, otherwise use the matchingPeriod license data
                const licenseData = existingLicense || license;

                const licenseSelected = !!existingLicense || (!selectedLicenses.length && licenseData.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE);

                // Set the selected property
                return {
                    ...licenseData,
                    selected: licenseSelected
                };
            });

            months.push({
                year: date.year(),
                startDate: startDate,
                endDate: endDate,
                label: date.format('MMMM YYYY'),
                selected: isSelectedOnLoad,
                licenses: licenses
            })
        }

        // default select to the most recent period if nothing has been selected
        const selectedFound = months.some(amonths => amonths.selected);

        // If no quarter is selected, find the most recent one
        if (!selectedFound) {
            const mostRecentMonth = months.reduce((prev, current) => {
                const prevStartDate = dayjs(prev.startDate);
                const currentStartDate = dayjs(current.startDate);
                return prevStartDate.isAfter(currentStartDate) ? prev : current;
            });

            if (mostRecentMonth) {
                mostRecentMonth.selected = true;
            }
        }

        months.sort((a, b) => {
            if (a.year !== b.year) {
                return b.year - a.year // Sort years in descending order
            }
            return dayjs(a.startDate).diff(dayjs(b.startDate)) // Sort months in ascending order
        })

        setAvailabelReportingPeriods(months);
    }

    const datesMatch = (dateOne, dateTwo) => {
        return dayjs(dateOne).isSame(dayjs(dateTwo, 'day'));
    }

    const generateReportingPeriodsByQuarters = (paymentPeriods) => {
        // Generate quarters for the current year and previous three years
        const currentYear = dayjs().year();
        const currentDate = dayjs();

        const quarters = [];

        for (let year = currentYear; year > currentYear - 4; year--) {
            for (let quarter = 1; quarter <= 4; quarter++) {
                const { startDate, endDate } = getQuarterStartEndDates(year, quarter);
                const date = dayjs(new Date(year, (quarter - 1) * 3, 1));

                // Check if the end date of the quarter is within the last 36 months
                const monthsDifference = currentDate.diff(endDate, 'months');

                if (endDate.isBefore(currentDate) && monthsDifference >= 0 && monthsDifference <= 36) {

                    // Find payment periods matching the start and end dates 
                    const matchingPeriod = paymentPeriods.find(period => datesMatch(period.startDate, startDate) && datesMatch(period.endDate, endDate));

                    const selectedLicenses = formData?.selectedLicenses ?? [];
                    const existingData = selectedLicenses.find(period => datesMatch(period.startDate, startDate) && datesMatch(period.endDate, endDate));

                    let isSelectedOnLoad = false;
                    if (preSelectedPeriod && preSelectedPeriod.startDate && preSelectedPeriod.endDate) {
                        const startDateMatchPreSelected = datesMatch(preSelectedPeriod.startDate, startDate);
                        const endDateMatchPreSelected = datesMatch(preSelectedPeriod.endDate, endDate);
                        if (startDateMatchPreSelected && endDateMatchPreSelected) {
                            isSelectedOnLoad = true;
                        }
                    }
                    else if (existingData) {
                        isSelectedOnLoad = true;
                    }

                    // Add selected property to all licenses if matching period found, otherwise use empty array
                    const licenses = (matchingPeriod?.licenses ?? []).map(license => {
                        // Check for matching license in existingData
                        const existingLicense = existingData?.licenses.find(existing => existing.license === license.license);
                        const licenseData = {
                            ...license,
                            ...existingLicense  // If existingLicense is null or undefined, spreading it has no effect
                        };

                        const licenseSelected = !!existingLicense || (!selectedLicenses.length && licenseData.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE);

                        // Set the selected property
                        return {
                            ...licenseData,
                            selected: licenseSelected
                        };
                    });

                    quarters.push({
                        year: date.year(),
                        startDate: startDate.format('YYYY-MM-DD'),
                        endDate: endDate.format('YYYY-MM-DD'),
                        label: `${startDate.format('MMM')} - ${endDate.format('MMM YYYY')}`,
                        selected: isSelectedOnLoad,
                        licenses: licenses,
                    });
                }
            }
        }

        //default select to the most recent period if nothing has been selected
        const selectedFound = quarters.some(quarter => quarter.selected);

        // If no quarter is selected, find the most recent one
        if (!selectedFound) {
            const mostRecentQuarter = quarters.reduce((prev, current) => {
                const prevStartDate = dayjs(prev.startDate);
                const currentStartDate = dayjs(current.startDate);
                return prevStartDate.isAfter(currentStartDate) ? prev : current;
            });

            if (mostRecentQuarter) {
                mostRecentQuarter.selected = true;
            }
        }

        quarters.sort((a, b) => {
            if (a.year !== b.year) {
                return b.year - a.year; // Sort years in descending order
            }
            return a.quarter - b.quarter; // Sort quarters in ascending order
        });

        setAvailabelReportingPeriods(quarters);
    };

    const toggleSelectableTaxablePeriods = (period) => {
        let updatedReportingPeriods = [...availabelReportingPeriods];

        const existingPeriod = updatedReportingPeriods?.find(item => (
            item.startDate === period.startDate && item.endDate === period.endDate
        ));

        if (existingPeriod) {

            if (existingPeriod.selected) { // currently selected, we need to deselct all licenses for the period
                deSelectAllLicensesInPeriod(existingPeriod);
            }
            else { // pre select licesnes for the period
                preSelectLicensesInPeriod(existingPeriod);
            }
            existingPeriod.selected = !existingPeriod.selected;
            setAvailabelReportingPeriods(updatedReportingPeriods);
            setShowNoSelectedLicneseError(false);
        }
    }

    const preSelectLicensesInPeriod = (period) => {
        period.licenses = period.licenses.map(license => ({
            ...license,
            selected: license.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE
        }));
    }

    const deSelectAllLicensesInPeriod = (period) => {
        period.licenses = period.licenses.map(license => ({
            ...license,
            selected: false
        }));
    }

    const toggleSelectedLicenseForPeriod = (licenseNum, period) => {
        let updatedReportingPeriods = [...availabelReportingPeriods];

        const existingPeriod = updatedReportingPeriods?.find(item => (
            item.startDate === period.startDate && item.endDate === period.endDate
        ));

        if (existingPeriod) {
            let exisitngLicense = existingPeriod.licenses.find(license => license.license === licenseNum);

            exisitngLicense.selected = !exisitngLicense.selected;
            setAvailabelReportingPeriods(updatedReportingPeriods);
            setShowNoSelectedLicneseError(false);
        }
    }

    const toggleSelectAllLicensesForPeriod = (period, isSelected) => {
        let updatedReportingPeriods = [...availabelReportingPeriods];

        const existingPeriod = updatedReportingPeriods?.find(item => (
            item.startDate === period.startDate && item.endDate === period.endDate
        ));

        if (existingPeriod) {
            existingPeriod.licenses = existingPeriod.licenses.map(license => ({
                ...license,
                selected: isSelected
            }));

            setAvailabelReportingPeriods(updatedReportingPeriods);
        }
    }

    const handleNextClick = () => {
        let valuesToSave = [...selectedReportingPeriods];

        // Check if at least one license has been selected for any year
        const selectedData = valuesToSave
            .filter(period => period.selected && period.licenses.some(license => license.selected)) // Filter selected periods with at least one selected license
            .map(period => ({
                ...period,
                licenses: period.licenses.filter(license => license.selected) // Filter licenses to include only selected ones
            }));

        const hasSelectedPeriodAndLicense = selectedData?.some(period =>
            period.licenses && period.licenses.length > 0
        );

        if (hasSelectedPeriodAndLicense) {
            // clear pre-selected payment periods in redux
            dispatch(clearPreSelectedTaxPeriod());
            // Save data in redux
            dispatch(updateTaxFlowData({ selectedLicenses: selectedData }));
            return true;
        }
        else {
            setShowNoSelectedLicneseError(true);
            return false;
        }
    }

    const handleBackClick = () => {
        dispatch(resetTaxState());
        placeNavigate("/dashboard");
    }

    return (
        <FlexboxGrid justify='space-between'>
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.PAY_TOT} />
            </FlexboxGrid.Item>

            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title='Select Properties for Tax Reporting'
                        onNext={handleNextClick}
                        onBack={() => { handleBackClick(); }}
                        currentStep={1}
                        flowProgressStepsSelector={selectTaxSteps}
                    >
                        <Text className='mb-24'>Your properties that are registered for tax reporting and remittance in the selected period are listed below. Please select the properties for which you wish to report at this time.</Text>
                        {
                            paymentPeriodsInProgress ?
                                <IconIndicator /> :
                                <div>
                                    {
                                        paymentPeriodsSuccess ?
                                            selectedReportingPeriods.length ?
                                                selectedReportingPeriods?.map((period) => {
                                                    return (
                                                        <div className='mb-24' key={`tax_period_${period.startDate}_${period.endDate}`}>
                                                            <Text className='text-bold  header-text-h4 mb-16'>{period.label}</Text>
                                                            {
                                                                period.licenses.length ?
                                                                    <Grid fluid className='tax-table' sp>
                                                                        <FlexboxGrid className='tax-column-heading-row' align='bottom' as={Row}>
                                                                            <FlexboxGrid.Item as={Col} xs={24} md={2}>
                                                                                <div role="grid">
                                                                                    <FlexboxGrid as={Row} align='bottom' gutter={0}>
                                                                                        <FlexboxGrid.Item as={Col}>
                                                                                            <BasicStyledCheckbox onChange={(checked) => {
                                                                                                toggleSelectAllLicensesForPeriod(period, checked)
                                                                                            }} />
                                                                                        </FlexboxGrid.Item>
                                                                                        <FlexboxGrid.Item as={Col} mdHidden lgHidden xlHidden xxlHidden>Select/Unselect all</FlexboxGrid.Item>
                                                                                    </FlexboxGrid>
                                                                                </div>
                                                                            </FlexboxGrid.Item>
                                                                            <FlexboxGrid.Item as={Col} xs={22} xsHidden smHidden mdHidden={false}>
                                                                                <div role="grid">
                                                                                    <FlexboxGrid as={Row} align='bottom'>
                                                                                        <FlexboxGrid.Item as={Col} xs={22} md={9}>Address</FlexboxGrid.Item>
                                                                                        <FlexboxGrid.Item as={Col} xs={12} md={5}>{permitType} Number</FlexboxGrid.Item>
                                                                                        <FlexboxGrid.Item as={Col} xs={6} md={5}>Tax Reporting This Period</FlexboxGrid.Item>
                                                                                        <FlexboxGrid.Item as={Col} xs={6} md={5}>Owner's Name</FlexboxGrid.Item>
                                                                                    </FlexboxGrid>
                                                                                </div>
                                                                            </FlexboxGrid.Item>
                                                                        </FlexboxGrid>
                                                                        <div className='tax-license-row-wrapper'>
                                                                            {
                                                                                period?.licenses.map((license, index) => (
                                                                                    (
                                                                                        <Row key={`license_${index}`} className='tax-license-row'>
                                                                                            <Col xs={3} md={2}>
                                                                                                <BasicStyledCheckbox
                                                                                                    onChange={() => { toggleSelectedLicenseForPeriod(license.license, period) }}
                                                                                                    checked={license.selected}
                                                                                                />
                                                                                            </Col>
                                                                                            <Col xs={21} md={22}>
                                                                                                <div role="grid">
                                                                                                    <Row>
                                                                                                        <Col xs={22} md={9} className='select-properties-address'>{getAddressWithUnitNumber(license.address, license.unitNumber)}</Col>
                                                                                                        <Col xs={12} md={5}>{license.license}</Col>
                                                                                                        <Col xs={12} md={5}>
                                                                                                            <TaxReportingStatusIndicator status={license.totPaidStatus} />
                                                                                                            {titleize(license.totPaidStatus ?? '')}
                                                                                                        </Col>
                                                                                                        <Col xs={24} md={5}>{license.ownerName}</Col>
                                                                                                    </Row>
                                                                                                </div>
                                                                                            </Col>
                                                                                        </Row>
                                                                                    )
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    </Grid>
                                                                    : <Text>No {pluralize(permitType)} available for this period</Text>
                                                            }
                                                        </div>
                                                    )
                                                })
                                                :
                                                <Notice message="No Payment periods Selected" type='info' />
                                            : <Notice style={{ marginBottom: '15px' }} className message={paymentPeriodsErrorMessage} />
                                    }

                                    {
                                        showNoSelectedLicneseError && (
                                            <Notice message='Please select one or more licenses to continue' className="mb-8" />
                                        )
                                    }


                                    <div className='responsive-stack'>
                                        <Button
                                            className='btn-tertiary sm-100-percent'
                                            onClick={() => setAdditionalPeriodsModalOpen(true)}
                                        >
                                            Add Additional Periods
                                        </Button>

                                        <BasicStyledCheckbox
                                            onChange={(checked) => {
                                                setHideInactivePermits(checked);
                                            }}
                                            checked={hideInactivePermits}
                                            label={`Hide Inactive ${pluralize(permitType)}`}
                                        >
                                        </BasicStyledCheckbox>
                                    </div>
                                </div>
                        }
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
            <AddAdditionalPeriodsModal
                availabelReportingPeriods={availabelReportingPeriods}
                updateAvailableReportingPeriods={toggleSelectableTaxablePeriods}
                isOpen={additionalPeriodsModalOpen}
                setOpen={setAdditionalPeriodsModalOpen}
                taxPaymentPeriod={taxPaymentPeriod}
            />
        </FlexboxGrid>
    )
}

export default TaxSelectProperties
